import { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';

/**
 * IdleTimeoutDialog - Drop-in component that monitors inactivity and shows a
 * MUI warning dialog before logging the user out.
 *
 * Usage:
 *   <IdleTimeoutDialog />                              // 30 min timeout, 2 min warning
 *   <IdleTimeoutDialog idleTimeout={15 * 60 * 1000} /> // 15 min timeout
 *   <IdleTimeoutDialog
 *     idleTimeout={30 * 60 * 1000}
 *     warningDuration={3 * 60 * 1000}
 *     title="Still there?"
 *     message="Your session will expire soon."
 *     buttonLabel="Keep Working"
 *     redirectUrl="/sign-in?expired=true"
 *     globalSignOut={true}
 *     enabled={isAuthenticated}
 *     onLogout={() => navigate('/login')}
 *   />
 *
 * @param {Object} props
 * @param {number}   props.idleTimeout      - Idle time before warning in ms (default: 30 min)
 * @param {number}   props.warningDuration  - Extra time after warning before logout in ms (default: 2 min)
 * @param {string}   props.title            - Dialog title (default: "Session Expiring Soon")
 * @param {string}   props.message          - Dialog body text (default: auto-generated from warningDuration)
 * @param {string}   props.buttonLabel      - Stay-logged-in button text (default: "Stay Logged In")
 * @param {string}   props.redirectUrl      - Redirect URL after logout (default: '/login?reason=idle')
 * @param {boolean}  props.globalSignOut    - Revoke refresh token on logout (default: true)
 * @param {boolean}  props.enabled          - Enable/disable the timeout (default: true)
 * @param {function} props.onLogout         - Custom logout handler (overrides redirectUrl)
 * @param {string[]} props.activityEvents   - DOM events that count as activity (default: mousemove, keydown, scroll, click, touchstart)
 */
export default function IdleTimeoutDialog({
    idleTimeout = 30 * 60 * 1000,
    warningDuration = 2 * 60 * 1000,
    title = 'Session Expiring Soon',
    message,
    buttonLabel = 'Stay Logged In',
    redirectUrl = '/login?reason=idle',
    globalSignOut = true,
    enabled = true,
    onLogout,
    activityEvents,
}) {
    const [open, setOpen] = useState(false);
    const [countdown, setCountdown] = useState(Math.floor(warningDuration / 1000));

    const defaultMessage = `You'll be logged out in ${Math.floor(warningDuration / 60000)} minute${Math.floor(warningDuration / 60000) !== 1 ? 's' : ''
        } due to inactivity.`;

    const { stayLoggedIn } = useIdleTimeout({
        idleTimeout,
        warningDuration,
        globalSignOut,
        redirectUrl,
        enabled,
        ...(activityEvents && { activityEvents }),
        onWarning: () => {
            setCountdown(Math.floor(warningDuration / 1000));
            setOpen(true);
        },
        onLogout,
        onActive: () => setOpen(false),
    });

    // Live countdown while warning is shown
    useEffect(() => {
        if (!open) return;

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [open]);

    const handleStayLoggedIn = useCallback(() => {
        setOpen(false);
        stayLoggedIn();
    }, [stayLoggedIn]);

    const formatCountdown = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        if (m > 0) return `${m}:${String(s).padStart(2, '0')}`;
        return `${s}s`;
    };

    return (
        <Dialog
            open={open}
            disableEscapeKeyDown
            onClose={(_, reason) => {
                if (reason !== 'backdropClick') handleStayLoggedIn();
            }}
            aria-labelledby="idle-timeout-dialog-title"
            aria-describedby="idle-timeout-dialog-description"
        >
            <DialogTitle id="idle-timeout-dialog-title">{title}</DialogTitle>
            <DialogContent>
                <DialogContentText id="idle-timeout-dialog-description">
                    {message || defaultMessage}
                </DialogContentText>
                <DialogContentText
                    sx={{ mt: 1, fontWeight: 'bold', fontVariantNumeric: 'tabular-nums' }}
                >
                    Time remaining: {formatCountdown(countdown)}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleStayLoggedIn} variant="contained" autoFocus>
                    {buttonLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
}